// Client-side handling of Jupiter /swap-instructions responses, a port of
// sdk/rust/src/jupiter.rs (docs/01-architecture.md section 7.1 steps 2-4, 6):
// the fixed route arguments of the four allow-listed instructions, the layout
// validator, the Execute assembler, the ATA helpers, the protocol fee's side
// and sizing, and a small HTTP client. Pinned against Rust by
// sdk/fixtures/jupiter-assembly-v1.json and the M4 fixtures under
// sdk/fixtures/jupiter/ (test/jupiter.test.ts).
//
// The protocol fee (docs/01-architecture.md §7.7, program/src/fee.rs): the
// side that pays is fixed by the two signed mints (`feeSide`): a stablecoin
// side (USDC, USDT; the output when both are), otherwise a SOL (wrapped SOL
// mint) side, otherwise the output. On the output side the fee is taken from
// the gross output and `maxIn` bounds the input alone; on the input side the
// fee is taken from the input account after the swap, `maxIn` bounds the
// input spent plus the fee (`maxInForRoute`), and the user keeps the whole
// output.

import { address, getProgramDerivedAddress } from '@solana/kit';
import { type AccountMetaSpec, type ExecHeaderSpec, type InnerIx, type JupSlots, MAX_AUX, MAX_INNER_ACCOUNTS, U64_MAX, executeAccounts, executeDigest, executeInstructionData, hex, jupiterSlots } from './digest.js';
import { equalBytes } from './recovery.js';
import { type Instruction, type Limits, MAX_LOADED_ACCOUNTS_DATA_SIZE, type V1Message, checkV1, compileV1 } from './v1.js';
import { addressBytes, addressFromBytes } from './vault.js';

export const JUPITER_ADDRESS = 'JUP6LkbZbjS1jKKwapdHNy74zcZ3tLUZoi5QNyVTaV4';
export const ATA_PROGRAM_ADDRESS = 'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL';
/** The native (wrapped SOL) mint: the program's fee-side SOL mint (program/src/fee.rs `NATIVE_MINT`). */
export const WSOL_MINT_ADDRESS = 'So11111111111111111111111111111111111111112';
/** Mainnet USDC, a fee-side stablecoin (program/src/fee.rs `USDC_MINT`). */
export const USDC_MINT_ADDRESS = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';
/** Mainnet USDT, a fee-side stablecoin (program/src/fee.rs `USDT_MINT`). */
export const USDT_MINT_ADDRESS = 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB';
export const JUPITER: Uint8Array = addressBytes(address(JUPITER_ADDRESS));
export const ATA_PROGRAM: Uint8Array = addressBytes(address(ATA_PROGRAM_ADDRESS));
export const WSOL_MINT: Uint8Array = addressBytes(address(WSOL_MINT_ADDRESS));
export const USDC_MINT: Uint8Array = addressBytes(address(USDC_MINT_ADDRESS));
export const USDT_MINT: Uint8Array = addressBytes(address(USDT_MINT_ADDRESS));
export const SYSTEM_PROGRAM: Uint8Array = new Uint8Array(32);
/** Discriminators (hex) of the allow-listed instructions. */
export const DISC = { route: 'e517cb977ae3ad2a', sharedAccountsRoute: 'c1209b3341d69c81', routeV2: 'bb64facc31c4af14', sharedAccountsRouteV2: 'd19853937cfed8e9' } as const;
/** Public AMMs the client quotes with for a program-owned user (section 7.1 step 2; a proposed policy, see docs/03 "Coder notes after M4"). */
export const PUBLIC_DEXES = ['Raydium', 'Raydium CLMM', 'Raydium CP', 'Meteora DLMM', 'Meteora DAMM v2', 'Lifinity V2', 'Pump.fun Amm', 'Phoenix', 'OpenBook V2'];
/**
 * Venues quoted for a SOL-LST pair only (docs/19-staking-plan.md sections 3.2 and 3.4, owner decision 9; proposed
 * policy, to the reviewer with ST4): depth-4 venues whose LST routes were replayed through the vault in both
 * directions (`docs/evidence/staking/2026-10-05/lst-replay/matrix.json`, one snapshot each). Every other pair keeps
 * `PUBLIC_DEXES`, so adding a label here never widens SOL-USDC.
 */
export const LST_VENUES = ['Meteora DLMM', 'Raydium CLMM', 'Manifest', 'Whirlpool', 'Sanctum Infinity', 'Sanctum Prop S'];
export interface LstMint { symbol: string; mint: string; decimals: number }
/** The LSTs offered with `LST_VENUES` (decision 9); mints from the pinned fixtures in sdk/fixtures/jupiter-lst/, decimals from the mint accounts there. */
export const LST_MINTS: readonly LstMint[] = [
  { symbol: 'JitoSOL', mint: 'J1toso1uCk3RLmjorhTtrVwY9HJ7X8V9yYac6Y7kGCPn', decimals: 9 },
  { symbol: 'JupSOL', mint: 'jupSoLaHXQiZZTSfEWMTRRgpnyFm8f6sZdosWBjx93v', decimals: 9 },
  { symbol: 'mSOL', mint: 'mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So', decimals: 9 },
  { symbol: 'INF', mint: '5oVNBeEEQvYi1cX3ir8Dx5n1P7pdxydbGF2X4TxVusJm', decimals: 9 },
];
/** The listed LST with this mint (base58), if any. */
export function lstOf(mint: string): LstMint | undefined {
  return LST_MINTS.find((l) => l.mint === mint);
}
/**
 * The venue list for a pair: `LST_VENUES` when exactly one side is a listed LST and the other is wrapped SOL,
 * `PUBLIC_DEXES` for every other pair, and `null` for a pair of two listed LSTs (not offered).
 */
export function swapVenues(inMint: string, outMint: string): { dexes: string[]; lst: LstMint | null } | null {
  const a = lstOf(inMint);
  const b = lstOf(outMint);
  if (a && b) return null;
  const lst = a ?? b;
  if (lst && (a ? outMint : inMint) === WSOL_MINT_ADDRESS) return { dexes: [...LST_VENUES], lst };
  return { dexes: [...PUBLIC_DEXES], lst: null };
}
/**
 * `useSharedAccounts` for a SOL-LST quote (docs/19 section 3.4 item 2): true for a multi-step route (the API's
 * non-shared multi-step form is one the client refuses, `NonSharedMultiHop`), false for one step.
 */
export function lstSharedAccounts(quote: QuoteResponse): boolean {
  return quote.routePlan.length > 1;
}

// API shapes (camelCase as the API sends them).
export interface ApiAccountMeta { pubkey: string; isSigner: boolean; isWritable: boolean }
export interface ApiInstruction { programId: string; accounts: ApiAccountMeta[]; data: string }
export interface SwapInstructionsResponse {
  swapInstruction: ApiInstruction;
  setupInstructions?: ApiInstruction[];
  cleanupInstruction?: ApiInstruction | null;
  computeBudgetInstructions?: ApiInstruction[];
  otherInstructions?: ApiInstruction[];
  addressLookupTableAddresses?: string[];
  computeUnitLimit?: number;
}
export interface SwapInfo { ammKey: string; label?: string; inputMint: string; outputMint: string; inAmount: string; outAmount: string }
export interface RoutePlanEntry { swapInfo: SwapInfo; percent?: number; bps?: number }
export interface QuoteResponse {
  inputMint: string;
  outputMint: string;
  inAmount: string;
  outAmount: string;
  otherAmountThreshold: string;
  slippageBps: number;
  routePlan: RoutePlanEntry[];
  contextSlot?: number;
  instructionVersion?: string;
}

export type JupiterErrorCode =
  | 'WrongProgram' | 'BadAddress' | 'BadBase64' | 'UnknownDiscriminator' | 'DataTooShort' | 'TooManyMetas' | 'TooFewMetas'
  | 'AuthorityNotVault' | 'SourceMismatch' | 'DestinationMismatch' | 'MintMismatch' | 'ForeignVaultAccount' | 'UnexpectedSigner'
  | 'MaxInBelowRoute' | 'MaxInBelowRoutePlusFee' | 'NonSharedMultiHop' | 'BadSlippage' | 'TooManyAux' | 'Http' | 'NoRouteFits';

export class JupiterError extends Error {
  constructor(readonly code: JupiterErrorCode, message: string, readonly detail: Record<string, unknown> = {}) {
    super(message);
    this.name = 'JupiterError';
  }
}

/** The fixed arguments of a route instruction; `platformFeeBps` is a u8 in the V1 layouts and a u16 in V2; `positiveSlippageBps` exists only in V2. */
export interface RouteArgs {
  inAmount: bigint;
  quotedOutAmount: bigint;
  slippageBps: number;
  platformFeeBps: number;
  positiveSlippageBps?: number;
  /** Byte offset of the `route_plan` vector (its u32 length prefix). */
  routePlanOffset: number;
}

export function base64Decode(s: string): Uint8Array {
  try {
    if (!/^[A-Za-z0-9+/]*={0,2}$/.test(s)) throw new Error('alphabet');
    return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
  } catch {
    throw new JupiterError('BadBase64', 'instruction data is not base64');
  }
}

/**
 * V1 (`route`, `shared_accounts_route`): `[id u8]? || route_plan || in_amount u64 || quoted_out u64 || slippage u16 || platform_fee u8`
 * (the fixed fields are the last 19 bytes). V2 (`route_v2`, `shared_accounts_route_v2`): `[id u8]? || in_amount u64 ||
 * quoted_out u64 || slippage u16 || platform_fee u16 || positive_slippage u16 || route_plan`.
 */
export function decodeRouteArgs(data: Uint8Array): { discriminator: Uint8Array; args: RouteArgs } {
  if (data.length < 8) throw new JupiterError('DataTooShort', `data is ${data.length} bytes`, { have: data.length, need: 8 });
  const disc = data.slice(0, 8);
  const d = hex(disc);
  const layout = { [DISC.route]: [false, false], [DISC.sharedAccountsRoute]: [false, true], [DISC.routeV2]: [true, false], [DISC.sharedAccountsRouteV2]: [true, true] }[d];
  if (!layout) throw new JupiterError('UnknownDiscriminator', `unknown discriminator ${d}`, { discriminator: d });
  const [v2, hasId] = layout;
  const base = 8 + (hasId ? 1 : 0);
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  if (v2) {
    const need = base + 22 + 4;
    if (data.length < need) throw new JupiterError('DataTooShort', `data is ${data.length} bytes, need ${need}`, { have: data.length, need });
    return { discriminator: disc, args: { inAmount: view.getBigUint64(base, true), quotedOutAmount: view.getBigUint64(base + 8, true), slippageBps: view.getUint16(base + 16, true), platformFeeBps: view.getUint16(base + 18, true), positiveSlippageBps: view.getUint16(base + 20, true), routePlanOffset: base + 22 } };
  }
  const need = base + 4 + 19;
  if (data.length < need) throw new JupiterError('DataTooShort', `data is ${data.length} bytes, need ${need}`, { have: data.length, need });
  const tail = data.length - 19;
  return { discriminator: disc, args: { inAmount: view.getBigUint64(tail, true), quotedOutAmount: view.getBigUint64(tail + 8, true), slippageBps: view.getUint16(tail + 16, true), platformFeeBps: data[tail + 18]!, routePlanOffset: base } };
}

/** Jupiter's own ExactIn floor as `otherAmountThreshold` reports it: `quoted - floor(quoted * bps / 10_000)`, bps clamped at 100 %. */
export function jupiterMinOut(quotedOutAmount: bigint, slippageBps: number): bigint {
  const bps = BigInt(Math.min(slippageBps, 10_000));
  return quotedOutAmount - (quotedOutAmount * bps) / 10_000n;
}

/** Positions of the source and destination mint accounts in a route's metas (`route` carries only the destination mint). */
export function mintSlots(discriminator: Uint8Array): { src?: number; dst?: number } | undefined {
  return { [DISC.route]: { dst: 5 }, [DISC.sharedAccountsRoute]: { src: 7, dst: 8 }, [DISC.routeV2]: { src: 3, dst: 4 }, [DISC.sharedAccountsRouteV2]: { src: 6, dst: 7 } }[hex(discriminator)];
}

/** What the client knows and the program will hold the route to. */
export interface Expected {
  vault: Uint8Array;
  inAta: Uint8Array;
  outAta: Uint8Array;
  inMint: Uint8Array;
  outMint: Uint8Array;
  /** Every other token account owned by the vault: the program rejects any of them in the account list; the client refuses first. */
  otherVaultTokenAccounts: Uint8Array[];
  /**
   * The treasury's token account for the fee mint, where the protocol fee
   * lands: the output mint or the input mint as `feeSide(inMint, outMint)`
   * decides (`swapFeeAccount`; docs/01-architecture.md §7.7). Appended to the
   * aux range so it is signed with the rest of the Execute.
   */
  feeAccount: Uint8Array;
}

export interface ValidatedRoute {
  discriminator: Uint8Array;
  slots: JupSlots;
  args: RouteArgs;
  /** The inner instruction as the program receives it (`is_signer` scrubbed on every meta). */
  inner: InnerIx;
  inMintPresent: boolean;
  outMintPresent: boolean;
}

function parseAddress(s: string): Uint8Array {
  try {
    return addressBytes(address(s));
  } catch {
    throw new JupiterError('BadAddress', `bad address ${s}`, { address: s });
  }
}

/**
 * Check a `swapInstruction` against the allow-list, the slot table and the
 * expected accounts, in the Rust client's order, and convert it into the
 * program's inner instruction. Token-account ownership, the mint policy and
 * the on-chain scan for vault-owned accounts remain the program's checks.
 */
export function validateSwapInstruction(api: ApiInstruction, expect: Expected): ValidatedRoute {
  const program = parseAddress(api.programId);
  if (!equalBytes(program, JUPITER)) throw new JupiterError('WrongProgram', `not a Jupiter instruction: ${api.programId}`, { programId: api.programId });
  const data = base64Decode(api.data);
  const { discriminator, args } = decodeRouteArgs(data);
  const slots = jupiterSlots(discriminator);
  if (!slots) throw new JupiterError('UnknownDiscriminator', `unknown discriminator ${hex(discriminator)}`);
  if (args.slippageBps > 10_000) throw new JupiterError('BadSlippage', `slippage ${args.slippageBps} bps above 100 %`, { slippageBps: args.slippageBps });
  const d = hex(discriminator);
  if (d === DISC.route || d === DISC.routeV2) {
    const steps = new DataView(data.buffer, data.byteOffset).getUint32(args.routePlanOffset, true);
    if (steps > 1) throw new JupiterError('NonSharedMultiHop', `a non-shared route with ${steps} hops routes through the vault's own ATAs; re-quote with shared accounts or onlyDirectRoutes`, { steps });
  }
  if (api.accounts.length > MAX_INNER_ACCOUNTS) throw new JupiterError('TooManyMetas', `${api.accounts.length} metas`, { have: api.accounts.length });
  const need = Math.max(slots.authority, slots.src, slots.dst) + 1;
  if (api.accounts.length < need) throw new JupiterError('TooFewMetas', `${api.accounts.length} metas, need ${need}`, { have: api.accounts.length, need });
  const metas: { pubkey: Uint8Array; isWritable: boolean }[] = [];
  for (const m of api.accounts) {
    const pubkey = parseAddress(m.pubkey);
    if (m.isSigner && !equalBytes(pubkey, expect.vault)) throw new JupiterError('UnexpectedSigner', `the route needs a signer the relayer cannot supply: ${m.pubkey}`, { address: m.pubkey });
    metas.push({ pubkey, isWritable: m.isWritable });
  }
  const at = (i: number) => metas[i]!.pubkey;
  if (!equalBytes(at(slots.authority), expect.vault)) throw new JupiterError('AuthorityNotVault', 'the user_transfer_authority slot is not the vault', { found: hex(at(slots.authority)) });
  if (!equalBytes(at(slots.src), expect.inAta)) throw new JupiterError('SourceMismatch', 'the source slot is not the vault input ATA', { expected: hex(expect.inAta), found: hex(at(slots.src)) });
  if (!equalBytes(at(slots.dst), expect.outAta)) throw new JupiterError('DestinationMismatch', 'the destination slot is not the vault output ATA', { expected: hex(expect.outAta), found: hex(at(slots.dst)) });
  const ms = mintSlots(discriminator)!;
  for (const [slot, expected] of [[ms.src, expect.inMint], [ms.dst, expect.outMint]] as [number | undefined, Uint8Array][]) {
    if (slot !== undefined && slot < metas.length && !equalBytes(at(slot), expected)) throw new JupiterError('MintMismatch', 'a mint slot names a different mint than displayed', { expected: hex(expected), found: hex(at(slot)) });
  }
  for (const m of metas) {
    if (expect.otherVaultTokenAccounts.some((o) => equalBytes(o, m.pubkey))) throw new JupiterError('ForeignVaultAccount', 'another vault-owned token account appears in the route', { address: hex(m.pubkey) });
  }
  return {
    discriminator,
    slots,
    args,
    inner: { program, metas, data },
    inMintPresent: metas.some((m) => equalBytes(m.pubkey, expect.inMint)),
    outMintPresent: metas.some((m) => equalBytes(m.pubkey, expect.outMint)),
  };
}

export interface SwapParams {
  /** Executor and fee payer; signs the outer transaction. */
  relayer: Uint8Array;
  validUntilSlot: bigint;
  /** Upper bound on what the swap may take from the input account: the input spent on the output side, the input spent plus the protocol fee on the input side (`maxInForRoute`). */
  maxIn: bigint;
  /** Lower bound on what it must deliver to the output account: the gross delivery before the fee on the output side, what the user keeps on the input side. */
  minOut: bigint;
  nextKey?: Uint8Array;
  /** BOUNDED mode: leave the route unbound so a fresh route can be re-sent under the same signature. */
  bounded: boolean;
}

export interface SwapAssembly {
  header: ExecHeaderSpec;
  aux: AccountMetaSpec[];
  inners: InnerIx[];
}

/**
 * The Execute header, aux range `[input ATA, output ATA, relayer, missing mints..., treasury fee account]` and inner instruction of a swap (section 7.1 step 4).
 * `maxIn` must admit the route's `inAmount`, plus the protocol fee on it when the input side pays (`feeSide(expect.inMint, expect.outMint)`):
 * `MaxInBelowRoute` on the output side, `MaxInBelowRoutePlusFee` on the input side (also when the fee-inclusive bound exceeds a u64).
 */
export function assembleExecute(route: ValidatedRoute, expect: Expected, p: SwapParams): SwapAssembly {
  const inAmount = route.args.inAmount;
  if (feeSide(expect.inMint, expect.outMint) === 'output') {
    if (p.maxIn < inAmount) throw new JupiterError('MaxInBelowRoute', `max_in ${p.maxIn} is below the route's in_amount ${inAmount}`, { maxIn: p.maxIn.toString(), inAmount: inAmount.toString() });
  } else {
    const need = maxInForRoute('input', inAmount);
    if (need === null || p.maxIn < need) {
      const fee = protocolFee(inAmount);
      throw new JupiterError('MaxInBelowRoutePlusFee', `max_in ${p.maxIn} is below the route's in_amount ${inAmount} plus the protocol fee ${fee} the input side pays`, { maxIn: p.maxIn.toString(), inAmount: inAmount.toString(), fee: fee.toString() });
    }
  }
  const aux: AccountMetaSpec[] = [
    { pubkey: expect.inAta, isSigner: false, isWritable: true },
    { pubkey: expect.outAta, isSigner: false, isWritable: true },
    { pubkey: p.relayer, isSigner: true, isWritable: false },
  ];
  if (!route.inMintPresent) aux.push({ pubkey: expect.inMint, isSigner: false, isWritable: false });
  if (!route.outMintPresent) aux.push({ pubkey: expect.outMint, isSigner: false, isWritable: false });
  aux.push({ pubkey: expect.feeAccount, isSigner: false, isWritable: true });
  if (aux.length > MAX_AUX) throw new JupiterError('TooManyAux', `${aux.length} aux accounts`, { have: aux.length });
  const header: ExecHeaderSpec = {
    numAux: aux.length,
    validUntilSlot: p.validUntilSlot,
    executor: p.relayer,
    settle: { inIndex: 0, outIndex: 1, inMint: expect.inMint, outMint: expect.outMint, maxIn: p.maxIn, minOut: p.minOut },
  };
  if (p.nextKey) header.nextKey = p.nextKey;
  if (p.bounded) header.boundedIx = 0;
  return { header, aux, inners: [route.inner] };
}

export function swapDigest(asm: SwapAssembly, programId: Uint8Array, vaultId: Uint8Array, vaultAddress: Uint8Array, treeIndex: number, leaf: number): Uint8Array {
  return executeDigest({ programId, vaultId, vaultAddress, treeIndex, leaf, header: asm.header, aux: asm.aux.map((a) => a.pubkey), inners: asm.inners });
}

/** The Execute instruction around an already-produced signature. */
export function swapInstruction(asm: SwapAssembly, programId: Uint8Array, vault: Uint8Array, signature: Uint8Array): Instruction {
  return { programId, accounts: executeAccounts(vault, asm.aux, asm.inners), data: executeInstructionData(signature, asm.header, asm.inners) };
}

export interface TokenPrograms {
  inMint: Uint8Array;
  inTokenProgram: Uint8Array;
  outMint: Uint8Array;
  outTokenProgram: Uint8Array;
}

/** The associated token account of `owner` for `mint` under `tokenProgram`. */
export async function ataAddress(owner: Uint8Array, mint: Uint8Array, tokenProgram: Uint8Array): Promise<Uint8Array> {
  const [pda] = await getProgramDerivedAddress({ programAddress: address(ATA_PROGRAM_ADDRESS), seeds: [owner, tokenProgram, mint] });
  return addressBytes(pda);
}

/** Fee share in basis points of the settled amount: the gross output, or the input spent (program/src/fee.rs `FEE_BPS`). */
export const FEE_BPS = 30;
const BPS_DENOMINATOR = 10_000n;

/** The side of a swap the protocol fee is taken from (program/src/fee.rs `FeeSide`). */
export type FeeSide = 'input' | 'output';

const isStable = (mint: Uint8Array) => equalBytes(mint, USDC_MINT) || equalBytes(mint, USDT_MINT);

/**
 * The side of a swap that pays the protocol fee, decided by the two signed
 * mints alone, exactly as the program decides it (program/src/fee.rs
 * `fee_side`): a stablecoin side (USDC, USDT) if there is one, the output when
 * both are stablecoins; otherwise a SOL (wrapped SOL mint) side; otherwise the
 * output. SOL->BONK pays in SOL (input), BONK->USDC in USDC (output),
 * USDC->SOL in USDC (input), SOL->USDC in USDC (output), USDC->USDT in USDT
 * (output), BONK->WIF in WIF (output).
 */
export function feeSide(inMint: Uint8Array, outMint: Uint8Array): FeeSide {
  if (isStable(outMint)) return 'output';
  if (isStable(inMint)) return 'input';
  if (equalBytes(outMint, WSOL_MINT)) return 'output';
  if (equalBytes(inMint, WSOL_MINT)) return 'input';
  return 'output';
}

/** The paying side of a swap between `mints` and that side's mint and token program (Rust `TokenPrograms::fee_side` / `fee_mint`). */
export function feeMint(mints: TokenPrograms): { side: FeeSide; mint: Uint8Array; tokenProgram: Uint8Array } {
  const side = feeSide(mints.inMint, mints.outMint);
  return side === 'input' ? { side, mint: mints.inMint, tokenProgram: mints.inTokenProgram } : { side, mint: mints.outMint, tokenProgram: mints.outTokenProgram };
}

/**
 * `vault_id` of the owner's treasury vault (program/src/fee.rs
 * `TREASURY_VAULT_ID`, set 2026-09-24 with scripts/treasury-id.ts). Change
 * both in one commit.
 */
export const TREASURY_VAULT_ID: Uint8Array = Uint8Array.from([
  0x31, 0x18, 0xeb, 0x66, 0x1b, 0xa4, 0x39, 0x7b, 0x18, 0xeb, 0xf8, 0x9e, 0x94, 0x6c, 0x3b, 0xb1,
  0x4c, 0xf7, 0x68, 0xa1, 0x2c, 0x09, 0xfe, 0xe4, 0x99, 0x91, 0x55, 0xab, 0xda, 0xb8, 0x33, 0xf4,
]);

const SEED = new TextEncoder().encode('vault');

/** The treasury vault's address under `programId` (canonical PDA, same seeds as every other vault: `[SEED, vault_id]`). */
export async function treasuryAddress(programId: Uint8Array): Promise<Uint8Array> {
  const [pda] = await getProgramDerivedAddress({ programAddress: addressFromBytes(programId), seeds: [SEED, TREASURY_VAULT_ID] });
  return addressBytes(pda);
}

/**
 * The protocol fee account for `feeMint`: the treasury's associated token
 * account under `feeTokenProgram`. The fee mint is the paying side's mint
 * (`feeMint(mints)`, or `swapFeeAccount` in one step), not always the output
 * mint. The program accepts any treasury-owned token account of the mint; the
 * client always uses this one.
 */
export async function feeAccount(programId: Uint8Array, feeMint: Uint8Array, feeTokenProgram: Uint8Array): Promise<Uint8Array> {
  return ataAddress(await treasuryAddress(programId), feeMint, feeTokenProgram);
}

/** The protocol fee account of a swap between `mints`: `feeAccount` for the mint of the side `feeSide` picks. */
export async function swapFeeAccount(programId: Uint8Array, mints: TokenPrograms): Promise<Uint8Array> {
  const f = feeMint(mints);
  return feeAccount(programId, f.mint, f.tokenProgram);
}

/**
 * The protocol fee on a settled amount (`FEE_BPS`, floored; program/src/fee.rs
 * `fee_amount`): on the output side the fee on the gross output (the user
 * keeps `gross - fee`, and `minOut` binds the gross), on the input side the fee
 * on the input spent (the input account is debited `spent + fee`).
 */
export function protocolFee(amount: bigint): bigint {
  return (amount * BigInt(FEE_BPS)) / BPS_DENOMINATOR;
}

/**
 * The `maxIn` to sign for a route that spends `inAmount`: `inAmount` on the
 * output side; `inAmount + protocolFee(inAmount)` on the input side, where the
 * program bounds the input spent plus the fee. `null` when that bound exceeds
 * a u64 (no signable bound admits the route).
 */
export function maxInForRoute(side: FeeSide, inAmount: bigint): bigint | null {
  const need = side === 'input' ? inAmount + protocolFee(inAmount) : inAmount;
  return need > U64_MAX ? null : need;
}

/**
 * The largest route input a balance of `balance` pays for: `balance` on the
 * output side; on the input side the largest `a` with `a + protocolFee(a) <=
 * balance`, the amount to quote when the user spends "everything" (then sign
 * `maxInForRoute('input', a)`, which is at most `balance`). For a native
 * (wrapped SOL) input, `balance` is the spendable wSOL: the account's lamports
 * above its rent reserve. 1,000,000,000 gives 997,008,974 (+ 2,991,026 fee).
 *
 * `a + fee(a)` is strictly increasing in `a`, and the answer lies in
 * `floor(B·D/(D+b)) ..= floor((B·D + D - 1)/(D+b))` (b = `FEE_BPS`, D =
 * 10,000), at most two values; the larger one that fits wins.
 */
export function maxInputForBalance(side: FeeSide, balance: bigint): bigint {
  if (balance < 0n) return 0n;
  if (side === 'output') return balance;
  const d = BPS_DENOMINATOR;
  const b = BigInt(FEE_BPS);
  const lo = (balance * d) / (d + b);
  const hi = (balance * d + d - 1n) / (d + b);
  for (let a = hi; a >= lo; a--) if (a + protocolFee(a) <= balance) return a;
  return lo;
}

/** `createAssociatedTokenAccountIdempotent` with the relayer as payer. */
export function createAtaIdempotent(payer: Uint8Array, owner: Uint8Array, ata: Uint8Array, mint: Uint8Array, tokenProgram: Uint8Array): Instruction {
  return {
    programId: ATA_PROGRAM,
    accounts: [
      { pubkey: payer, isSigner: true, isWritable: true },
      { pubkey: ata, isSigner: false, isWritable: true },
      { pubkey: owner, isSigner: false, isWritable: false },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: SYSTEM_PROGRAM, isSigner: false, isWritable: false },
      { pubkey: tokenProgram, isSigner: false, isWritable: false },
    ],
    data: Uint8Array.of(1),
  };
}

/** The transaction body: create the output ATA, create the input ATA, create the treasury's fee account for the fee mint (`feeMint(mints)`: the output mint or the input mint), Execute (section 7.1 step 6). */
export async function transactionInstructions(asm: SwapAssembly, programId: Uint8Array, vault: Uint8Array, signature: Uint8Array, relayer: Uint8Array, mints: TokenPrograms): Promise<Instruction[]> {
  return transactionInstructionsAround(swapInstruction(asm, programId, vault, signature), programId, vault, relayer, mints);
}

/**
 * The same four-instruction body around an Execute instruction the caller
 * already holds: the one the signer returned (`Authorization.accounts` and
 * `.instructionData`), whose header may carry the automatic `next_key` the
 * signer added at the rotation boundary. Building Execute from the
 * authorization rather than re-serialising the caller's own assembly keeps
 * the sent message identical to the signed one (R43). The treasury's
 * account is created for the fee mint of the side that pays (`feeMint`); on
 * the output side the body is the same as before the fee side existed.
 */
export async function transactionInstructionsAround(execute: Instruction, programId: Uint8Array, vault: Uint8Array, relayer: Uint8Array, mints: TokenPrograms): Promise<Instruction[]> {
  const treasury = await treasuryAddress(programId);
  const fee = feeMint(mints);
  const feeAta = await ataAddress(treasury, fee.mint, fee.tokenProgram);
  return [
    createAtaIdempotent(relayer, vault, await ataAddress(vault, mints.outMint, mints.outTokenProgram), mints.outMint, mints.outTokenProgram),
    createAtaIdempotent(relayer, vault, await ataAddress(vault, mints.inMint, mints.inTokenProgram), mints.inMint, mints.inTokenProgram),
    createAtaIdempotent(relayer, treasury, feeAta, fee.mint, fee.tokenProgram),
    execute,
  ];
}

// HTTP client. Not exercised by tests; the M4 snapshot tool (scripts/snapshot-jupiter.ts) makes the same requests.

export interface QuoteRequest {
  inputMint: string;
  outputMint: string;
  amount: bigint | string;
  slippageBps: number;
  maxAccounts?: number;
  onlyDirectRoutes?: boolean;
  dexes?: string[];
  excludeDexes?: string[];
  restrictIntermediateTokens?: boolean;
  instructionVersion?: 'V1' | 'V2';
}

export interface JupiterClientOptions {
  /** `https://api.jup.ag/swap/v1` with an API key, `https://lite-api.jup.ag/swap/v1` without. */
  baseUrl?: string;
  apiKey?: string;
  fetch?: typeof fetch;
}

export class JupiterClient {
  private readonly baseUrl: string;
  private readonly headers: Record<string, string>;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: JupiterClientOptions = {}) {
    this.baseUrl = opts.baseUrl ?? (opts.apiKey ? 'https://api.jup.ag/swap/v1' : 'https://lite-api.jup.ag/swap/v1');
    this.headers = { accept: 'application/json', ...(opts.apiKey ? { 'x-api-key': opts.apiKey } : {}) };
    this.fetchImpl = opts.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => globalThis.fetch(input, init)); // bound: a bare `globalThis.fetch` called as a method throws "Illegal invocation" in browsers
  }

  /** `GET /quote`; quote with `dexes: PUBLIC_DEXES` for a program-owned user. */
  async quote(req: QuoteRequest): Promise<QuoteResponse> {
    const q = new URLSearchParams({ inputMint: req.inputMint, outputMint: req.outputMint, amount: String(req.amount), slippageBps: String(req.slippageBps) });
    if (req.maxAccounts !== undefined) q.set('maxAccounts', String(req.maxAccounts));
    if (req.onlyDirectRoutes) q.set('onlyDirectRoutes', 'true');
    if (req.restrictIntermediateTokens !== undefined) q.set('restrictIntermediateTokens', String(req.restrictIntermediateTokens));
    if (req.dexes?.length) q.set('dexes', req.dexes.join(','));
    if (req.excludeDexes?.length) q.set('excludeDexes', req.excludeDexes.join(','));
    if (req.instructionVersion) q.set('instructionVersion', req.instructionVersion);
    return this.json<QuoteResponse>(`${this.baseUrl}/quote?${q}`, { headers: this.headers });
  }

  /** `POST /swap-instructions` for the vault as user: no SOL wrapping, no RPC lookups by Jupiter. */
  async swapInstructions(quoteResponse: QuoteResponse, userPublicKey: string, opts: { useSharedAccounts?: boolean } = {}): Promise<SwapInstructionsResponse> {
    const body: Record<string, unknown> = { quoteResponse, userPublicKey, wrapAndUnwrapSol: false, skipUserAccountsRpcCalls: true };
    if (opts.useSharedAccounts !== undefined) body.useSharedAccounts = opts.useSharedAccounts;
    return this.json<SwapInstructionsResponse>(`${this.baseUrl}/swap-instructions`, { method: 'POST', headers: { ...this.headers, 'content-type': 'application/json' }, body: JSON.stringify(body) });
  }

  private async json<T>(url: string, init: RequestInit): Promise<T> {
    const res = await this.fetchImpl(url, init);
    if (!res.ok) throw new JupiterError('Http', `${init.method ?? 'GET'} ${url}: ${res.status}`, { status: res.status, body: await res.text() });
    return (await res.json()) as T;
  }
}

// The maxAccounts ladder (section 7.1 step 2, R19): quote with a shrinking
// account budget until the whole four-instruction transaction passes the
// pre-flight rules; every skipped rung is reported.

export const MAX_ACCOUNTS_LADDER: readonly number[] = [64, 48, 40, 32, 24];
/** The ceiling limits of the first simulation (section 7.1 step 6), used for the sizing check inside the ladder. The loaded-data figure is the protocol maximum and stays there after the simulation (R70). */
export const LADDER_LIMITS: Limits = { computeUnitLimit: 400_000, loadedAccountsDataSizeLimit: MAX_LOADED_ACCOUNTS_DATA_SIZE, priorityFeeLamports: 1_000n };
const PLACEHOLDER_SIGNATURE = new Uint8Array(1037);

export interface LadderInput {
  client: JupiterClient;
  request: Omit<QuoteRequest, 'maxAccounts'>;
  /** The vault address, base58, as Jupiter's `userPublicKey`. */
  user: string;
  expect: Expected;
  params: SwapParams;
  mints: TokenPrograms;
  programId: Uint8Array;
  vault: Uint8Array;
  ladder?: readonly number[];
  /** Sent on every rung; a function decides per quote (`lstSharedAccounts` for a SOL-LST pair). */
  useSharedAccounts?: boolean | ((quote: QuoteResponse) => boolean | undefined);
}

export interface LadderResult {
  maxAccounts: number;
  quote: QuoteResponse;
  swapInstructions: SwapInstructionsResponse;
  route: ValidatedRoute;
  assembly: SwapAssembly;
  /** The four-instruction body with a zero placeholder signature; the signer fills the real one. */
  instructions: Instruction[];
  message: V1Message;
  attempts: { maxAccounts: number; reason: string }[];
}

export async function quoteWithinLimits(i: LadderInput): Promise<LadderResult> {
  const attempts: { maxAccounts: number; reason: string }[] = [];
  for (const maxAccounts of i.ladder ?? MAX_ACCOUNTS_LADDER) {
    try {
      const quote = await i.client.quote({ ...i.request, maxAccounts });
      const useSharedAccounts = typeof i.useSharedAccounts === 'function' ? i.useSharedAccounts(quote) : i.useSharedAccounts;
      const swapInstructions = await i.client.swapInstructions(quote, i.user, { useSharedAccounts });
      const route = validateSwapInstruction(swapInstructions.swapInstruction, i.expect);
      const assembly = assembleExecute(route, i.expect, i.params);
      const instructions = await transactionInstructions(assembly, i.programId, i.vault, PLACEHOLDER_SIGNATURE, i.params.relayer, i.mints);
      const message = compileV1(i.params.relayer, instructions, new Uint8Array(32), LADDER_LIMITS);
      checkV1(message);
      return { maxAccounts, quote, swapInstructions, route, assembly, instructions, message, attempts };
    } catch (e) {
      attempts.push({ maxAccounts, reason: e instanceof Error ? `${e.name}: ${e.message}` : String(e) });
    }
  }
  throw new JupiterError('NoRouteFits', 'no rung of the maxAccounts ladder produced a route the vault can carry', { attempts });
}
